import { Group, Select } from "@mantine/core";
import {
  deploymentLabel,
  distinctValues,
  relativeTime,
  type CvUpdateRequestSchema,
  type DeploymentFilters,
  type DeploymentScope,
} from "@/lib/deployments";

/**
 * Picks one deployment out of the recent ones.
 *
 * Same shape as `StoreFilterBar`: a full-width row in the page body, each
 * control `flex`-sized so they share a wide row and reflow rather than
 * overflow. The deployment select is given the largest basis because its
 * option labels are long — scope, both versions, the release and the author.
 *
 * The filter options are derived from the deployments actually fetched rather
 * than from a fixed list, so an author or release that has not deployed
 * recently never appears as a choice that selects nothing.
 */
interface DeploymentPickerProps {
  /** Everything fetched, for deriving filter options. */
  requests: CvUpdateRequestSchema[];
  /** What survives the current filters, for the deployment dropdown. */
  matching: CvUpdateRequestSchema[];
  filters: DeploymentFilters;
  onFiltersChange: (filters: DeploymentFilters) => void;
  selected: string | null;
  onSelect: (requestId: string | null) => void;
}

export function DeploymentPicker({
  requests,
  matching,
  filters,
  onFiltersChange,
  selected,
  onSelect,
}: DeploymentPickerProps) {
  const authors = distinctValues(requests, (request) => request.author);
  const refs = distinctValues(requests, (request) => request.ansible_ref);
  const tags = distinctValues(
    requests,
    (request) => request.cv_pipeline_image_tag,
  );

  const options = matching.map((request) => ({
    value: request.request_id as string,
    label: `${deploymentLabel(request)} · ${relativeTime(request.created_at)}`,
  }));

  return (
    <Group wrap="wrap" gap="sm" align="flex-end" mb="md" w="100%">
      <Select
        aria-label="Deployment"
        label="Deployment"
        placeholder={
          options.length ? "Select a deployment" : "No deployments match"
        }
        data={options}
        value={selected}
        onChange={onSelect}
        disabled={options.length === 0}
        searchable
        // Long labels; the dropdown is readable where a truncated input is not.
        comboboxProps={{ width: 560, position: "bottom-start" }}
        style={{ flex: "4 1 360px" }}
        miw={280}
      />
      <Select
        aria-label="Filter by scope"
        label="Scope"
        placeholder="Any scope"
        data={[
          { value: "blast", label: "Fleet blast" },
          { value: "store", label: "Single store" },
        ]}
        value={filters.scope}
        onChange={(value) =>
          onFiltersChange({
            ...filters,
            scope: value as DeploymentScope | null,
          })
        }
        clearable
        style={{ flex: "0 1 150px" }}
        miw={130}
      />
      <Select
        aria-label="Filter by release"
        label="Release"
        placeholder="Any release"
        data={refs}
        value={filters.ansibleRef}
        onChange={(value) => onFiltersChange({ ...filters, ansibleRef: value })}
        disabled={refs.length === 0}
        clearable
        searchable
        style={{ flex: "2 1 220px" }}
        miw={180}
      />
      <Select
        aria-label="Filter by image tag"
        label="CV image"
        placeholder="Any image"
        data={tags}
        value={filters.imageTag}
        onChange={(value) => onFiltersChange({ ...filters, imageTag: value })}
        disabled={tags.length === 0}
        clearable
        searchable
        style={{ flex: "1 1 160px" }}
        miw={140}
      />
      <Select
        aria-label="Filter by author"
        label="Author"
        placeholder="Anyone"
        data={authors}
        value={filters.author}
        onChange={(value) => onFiltersChange({ ...filters, author: value })}
        disabled={authors.length === 0}
        clearable
        searchable
        style={{ flex: "1 1 160px" }}
        miw={140}
      />
    </Group>
  );
}
